import { useCallback, useEffect, useState } from "react"
import { AddWorkflowFormProps } from "./AddPatchScopeWorkflowForm.types"
// import WorkflowInfoForm from "./ScopeWorkflowForm/WorkflowInfoForm"
// import SectionForm from "./SectionForm/SectionForm"
// import OverviewForm from "./OverviewForm/OverviewForm"
import ScopeWorkflowFormType from "../../../utils/ScopeWorkflowFormType"
import { CAMERA_OPTICAL_DIAMETER, MAX_PIPE_DIAMETER_FOR_DEFAULT_ANGLES, MAX_SCOPE_WORKFLOW_SECTIONS, MAX_SECTION_ANGLES, MIN_PATCH_SECTION_SIZE } from "../../../utils/constants"
import useScopeWorkflowInputs from "../../../hooks/useAngularScopeWorkflowInputs"
import { showErrorToast } from "../../../utils/utils"
import { AngularScopeWorkflow, PatchScopeWorkflow } from "../../../types/starAPIResponseTypes"
import { StarAPIFetchState } from "../../../types/types"

import usePatchScopeWorkflowInputs from "../../../hooks/usePatchScopeWorkflowInputs"
import PatchWorkflowInfoForm from "./ScopeWorkflowForm/PatchWorkflowInfoForm"
import PatchOverviewForm from "./OverviewForm/PatchOverviewForm"
import PatchSectionForm from "./SectionForm/PatchSectionForm"
import WorkflowType from "../../../enums/workflowType"
import { getWorkflow } from "../../../service/workflowService"
import { STARAPIError } from "../../../service/service"

const usePatchScopeAddWorkflow = (props: AddWorkflowFormProps) => {
  const {
    claddingMaterials,
    insulationMaterials,
    locations,
    onSubmit,
    scopeWorkflowId,
    showForm,
    formType,
    onHide 
  } = props

  const [scopeWorkflow, setScopeWorkflow] = useState<StarAPIFetchState<PatchScopeWorkflow>>({ status: "idle" })
  const [currentSection, setCurrentSection] = useState<number>(0)
  const [currentForm, setCurrentForm] = useState<number>(0)

  const [{
    filename,
    assetId,
    location,
    assetTemperature,
    assetType,
    insulationMaterial,
    insulationThickness,
    claddingMaterial,
    claddingThickness,
    sectionWidths,
    sectionHeights,
    sectionPositions,
    cornerTypes,
    selectedCornerType,
    onAddPosition,
    setDefaultPositions,
    onAddSectionValues,
    setScopeWorkflowForm,
    onRemoveCurrentSectionInputs,
    clearInputs,
    isScopeValidWorkflowInfo,
    isValidSectionForm
  }] = usePatchScopeWorkflowInputs({
    locations: locations,
    insulationMaterials: insulationMaterials,
    claddingMaterials: claddingMaterials,
    currentSection: currentSection
  })

  useEffect(() => {
    const fetchScopeWorkflow = async () => {
      if (!scopeWorkflowId) {return}
      setScopeWorkflow({ status: "loading" })
      const response = await getWorkflow(scopeWorkflowId)

      if (response instanceof Error) {
        showErrorToast("Failed to load workflow scope.")
        setScopeWorkflow({ status: "error", error: response as STARAPIError })
        return
      }
      if (response.workflow_type !== WorkflowType.PATCH_SCOPE) {
        return
      }
      setScopeWorkflow({ status: "success", data: response as PatchScopeWorkflow})
    }
    fetchScopeWorkflow()
  }, [scopeWorkflowId])

  useEffect(() => {
    clearInputs()
    switch (formType) {
      case ScopeWorkflowFormType.VIEW:
        setCurrentForm(2)
        break
      default:
        setCurrentForm(0)
    }
    
    if (scopeWorkflow.status !== "success") {
      return
    }
    const patchWorkflow = scopeWorkflow.data

    if (patchWorkflow === null) {
      return
    }

    switch (formType) {
      case ScopeWorkflowFormType.VIEW:
        setCurrentForm(2)
        setScopeWorkflowForm(patchWorkflow)
        return
      case ScopeWorkflowFormType.EDIT:
        setCurrentForm(0)
        setScopeWorkflowForm(patchWorkflow)
        return
    }
    setCurrentForm(0)
  }, [showForm, formType, scopeWorkflow, setScopeWorkflowForm, clearInputs])

  useEffect(() => {
    if (sectionWidths.value.length === 0) { return }
    const width = parseInt(sectionWidths.value[currentSection].value)
    if (isNaN(width)) { return }
    if (width < MIN_PATCH_SECTION_SIZE) { return }
    setDefaultPositions(width)    
  }, [sectionWidths.value[currentSection]])


  const onNextForm = async () => {    
    if (currentForm === 0) {
      if (!isScopeValidWorkflowInfo()) {
        return
      }
      if (sectionWidths.value.length === 0) {
        onAddSection()
      }      
    }

    if (currentForm === 1) {
      if (!isValidSectionForm()) {
        return
      }
    }

    if (currentForm === forms.length - 1 && formType === ScopeWorkflowFormType.VIEW) {
      setCurrentForm(0)
      onHide()
      return
    }

    if (currentForm === forms.length - 1) {
      onSubmit(getScopeWorkflow())
      setCurrentForm(0)
      onHide()
      return
    }

    setCurrentForm(currentForm + 1)
  }


  const onAddSection = () => {
    if (sectionWidths.value.length >= MAX_SCOPE_WORKFLOW_SECTIONS) { return }
    onAddSectionValues()
    setCurrentSection(sectionWidths.value.length)
  }

  const onRemoveCurrentSection = useCallback(() => {  
    if (sectionWidths.value.length === 1) { return }
    setCurrentSection((prev) => (prev === 0 ? 0 : prev-1))
    onRemoveCurrentSectionInputs()
  }, [sectionWidths, onRemoveCurrentSectionInputs])

  const getSections = () => {
    const sections = sectionWidths.value.map((start, i) => {
      return [parseInt(start.value), parseInt(sectionHeights.value[i].value), parseInt(`${selectedCornerType[i]}`)]
    })

    return JSON.stringify(sections)
  }

  const getPositions = () => {
    return JSON.stringify(sectionPositions)
  }

  const getScopeWorkflow = () => {
    let workflowId = undefined

    if (scopeWorkflow.status === "success") {
      workflowId = scopeWorkflow.data?.workflow_id
    }
    return {
      workflow_id: workflowId ?? 0,
      workflow_type:  WorkflowType.PATCH_SCOPE.valueOf(),
      star_vision_version: null,
      filename: filename.value,
      asset_id: assetId.value,
      user_id: 0,
      client_upload_uuid: "",
      created_at: `${Math.floor(Date.now() / 1000)}`,
      cladding_material: claddingMaterial.values[claddingMaterial.selected],
      cladding_thickness: parseInt(claddingThickness.value),
      insulation_material: insulationMaterial.values[insulationMaterial.selected],
      insulation_thickness: parseInt(insulationThickness.value),
      asset_temp: parseInt(assetTemperature.value),
      asset_type: assetType.selected,
      location: location.values[location.selected],
      sections: getSections(),
      horizontal_positions: getPositions(),
      is_fully_uploaded: false
    }
  }

  const onSelectSection = (section: number) => {
    if (formType === ScopeWorkflowFormType.VIEW) { return }
    setCurrentForm(section)
  }

  

  const forms = [
    <PatchWorkflowInfoForm
      filename={filename}
      assetId={assetId}
      location={location}
      assetTemperature={assetTemperature}
      assetType={assetType}
      insulationMaterial={insulationMaterial}
      insulationThickness={insulationThickness}
      claddingMaterial={claddingMaterial}
      claddingThickness={claddingThickness}
      onSubmit={onNextForm}
    />,
    <PatchSectionForm 
      cornerTypes={cornerTypes}
      sectionWidths={sectionWidths.value}
      onChangeSectionWidths={sectionWidths.onChange}
      sectionHeights={sectionHeights.value}
      onChangeSectionHeights={sectionHeights.onChange}
      sectionPositions={sectionPositions}
      currentSection={currentSection}
      onAddSection={onAddSection}
      onRemoveCurrentSection={onRemoveCurrentSection}
      onSelectSection={(section: number) => setCurrentSection(section)}
    />,
    <PatchOverviewForm workflow={getScopeWorkflow()} />
  ]

  return [{
    scopeWorkflow,
    currentForm: forms[currentForm],
    forms: ["WorkflowInfoForm", "SectionForm", "Overview"],
    currentFormIndex: currentForm,
    onSelectForm: onSelectSection,
    onNext: onNextForm,
  }]
}

export default usePatchScopeAddWorkflow